import { useEffect } from 'react'
import { useArenaStore } from '../store'
import {
  WAY_BUILDINGS, DOMAIN_COLOR, projectToMinimap, type MinimapBox,
} from './wayfinding'
import { INPUT_PRIORITY, modalInputRouter } from '../input/modalInputRouter'

// The drawable schematic is BOX square; the district is wider than deep, so the
// projection letterboxes vertically inside it — which keeps north up and the
// east-west High Street reading horizontally, as it does on the ground.
const BOX: MinimapBox = { width: 168, height: 168, pad: 16 }
const RAD_TO_DEG = 180 / Math.PI

// Pre-projected building dots — the district is static, so this never changes.
const DOTS = WAY_BUILDINGS.map((b) => ({
  ...b,
  p: projectToMinimap(b.cx, b.cz, BOX),
  color: DOMAIN_COLOR[b.domain] ?? 'rgba(245,235,224,0.7)',
}))

/**
 * Corner minimap: a top-down schematic of the T-junction with the eight named
 * buildings as colour-coded dots and the player as a facing arrow. Toggled with
 * [M]. Gated exactly like the rest of the HUD.
 */
export function Minimap() {
  const isPlaying         = useArenaStore((s) => s.isPlaying)
  const isPaused          = useArenaStore((s) => s.isPaused)
  const activeInteraction = useArenaStore((s) => s.activeInteraction)
  const inspectorRunId    = useArenaStore((s) => s.inspectorRunId)
  const minimapVisible    = useArenaStore((s) => s.minimapVisible)
  const toggleMinimap     = useArenaStore((s) => s.toggleMinimap)
  const playerX           = useArenaStore((s) => s.playerX)
  const playerZ           = useArenaStore((s) => s.playerZ)
  const playerYaw         = useArenaStore((s) => s.playerYaw)

  // [M] toggles the map — only while actually playing, so it doesn't fire from
  // a login field or the pause menu.
  useEffect(() => {
    return modalInputRouter.register(INPUT_PRIORITY.world, (e) => {
      if (e.code !== 'KeyM') return false
      const st = useArenaStore.getState()
      if (!st.isPlaying || st.isPaused || st.activeInteraction || st.inspectorRunId) return false
      toggleMinimap()
      return true
    })
  }, [toggleMinimap])

  if (!isPlaying || isPaused || activeInteraction || inspectorRunId) return null
  if (!minimapVisible) return null

  const player = projectToMinimap(playerX, playerZ, BOX)
  const yawDeg = playerYaw * RAD_TO_DEG

  return (
    <div style={s.wrap} data-testid="minimap">
      <div style={s.header}>
        <span style={s.title}>ANIMUS QUARTER</span>
        <span style={s.compassN}>N</span>
      </div>
      <div style={{ ...s.plane, width: BOX.width, height: BOX.height }}>
        <svg width={BOX.width} height={BOX.height} style={s.svg}>
          {/* Streets: the east-west High Street and the north-south stem. */}
          <line
            x1={BOX.pad} y1={player0Z()} x2={BOX.width - BOX.pad} y2={player0Z()}
            stroke="rgba(245,235,224,0.16)" strokeWidth={7} strokeLinecap="round"
          />
          <line
            x1={stemX()} y1={player0Z()} x2={stemX()} y2={BOX.height - BOX.pad}
            stroke="rgba(245,235,224,0.16)" strokeWidth={7} strokeLinecap="round"
          />
          {/* Buildings. */}
          {DOTS.map((d) => (
            <g key={d.id}>
              <circle cx={d.p.x} cy={d.p.y} r={4} fill={d.color} stroke="rgba(11,13,16,0.55)" strokeWidth={1} />
              <text
                x={d.p.x} y={d.p.y + (d.cz < 0 ? -6.5 : 12)}
                textAnchor="middle" style={s.label}
              >
                {shortName(d.name)}
              </text>
            </g>
          ))}
        </svg>
        {/* Player arrow — CSS transform only, so it's composited not laid out. */}
        <div
          data-testid="minimap-player"
          style={{
            ...s.arrow,
            transform: `translate(${player.x}px, ${player.y}px) translate(-50%, -50%) rotate(${yawDeg}deg)`,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14">
            <path d="M7 1 L11.5 12 L7 9.3 L2.5 12 Z" fill="#fbbf24" stroke="rgba(11,13,16,0.6)" strokeWidth="0.8" />
          </svg>
        </div>
      </div>
      <div style={s.hint}><span style={s.keycap}>M</span> map</div>
    </div>
  )
}

/** Screen Y of world Z = 0 — the High Street centreline runs through here. */
function player0Z(): number {
  return projectToMinimap(0, 0, BOX).y
}
/** Screen X of world X = 0 — the stem runs down from here. */
function stemX(): number {
  return projectToMinimap(0, 0, BOX).x
}

/** ANIMUS HQ → HQ; everything else is already short. */
function shortName(name: string): string {
  return name === 'ANIMUS HQ' ? 'HQ' : name
}

const s: Record<string, React.CSSProperties> = {
  wrap: {
    position: 'fixed',
    top: '1rem',
    right: '1rem',
    padding: '0.55rem 0.6rem 0.5rem',
    background: 'rgba(61,64,91,0.75)',
    border: '1px solid rgba(245,235,224,0.12)',
    borderRadius: '0.875rem',
    backdropFilter: 'blur(8px)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
    zIndex: 15,
    pointerEvents: 'none',
    userSelect: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '0 0.15rem 0.4rem',
  },
  title: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.64rem',
    fontWeight: 900,
    letterSpacing: '0.08em',
    color: 'rgba(245,235,224,0.55)',
  },
  compassN: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.62rem',
    fontWeight: 900,
    color: 'rgba(245,235,224,0.4)',
    letterSpacing: '0.05em',
  },
  plane: {
    position: 'relative',
    background: 'rgba(28,30,44,0.55)',
    borderRadius: '0.5rem',
    overflow: 'hidden',
  },
  svg: {
    position: 'absolute',
    inset: 0,
    display: 'block',
  },
  label: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '7px',
    fontWeight: 800,
    letterSpacing: '0.03em',
    fill: 'rgba(245,235,224,0.72)',
  } as React.CSSProperties,
  arrow: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 14,
    height: 14,
    transformOrigin: 'center',
    willChange: 'transform',
    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
  },
  hint: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    margin: '0.4rem 0.15rem 0',
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.6rem',
    color: 'rgba(245,235,224,0.4)',
  },
  keycap: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.58rem',
    fontWeight: 700,
    padding: '0.05rem 0.28rem',
    background: 'rgba(245,235,224,0.1)',
    border: '1px solid rgba(245,235,224,0.2)',
    borderBottom: '2px solid rgba(245,235,224,0.1)',
    borderRadius: '0.22rem',
    color: 'rgba(245,235,224,0.75)',
  },
}
